// Controlled vocabulary for hr_profiles.department. The HR department field
// is a fixed subsidiary list — free-text entry historically let garbage values
// (e.g. a stray year like "2013") leak into the directory filter. The same set
// is enforced server-side (ALLOWED_DEPARTMENTS in api/src/index.ts). null/blank
// is allowed and surfaces as "Unassigned".
export const ALLOWED_DEPARTMENTS = ['Construction', 'Painting', 'Windows & Doors'] as const

export type Stage = 'Lead' | 'Qualified' | 'Proposal' | 'Negotiation' | 'Active'
export type ProjectHealth = 'On track' | 'At risk' | 'Needs review'
export type ProjectLane = 'Backlog' | 'In progress' | 'Review' | 'Done'
export type CalendarId = 'estimates' | 'projects' | 'internal' | 'sub_project_starts'

export type CalendarSource = {
  id: CalendarId
  name: string
  color: string
}

export type TeamMember = {
  name: string
  role: string
  accounts: number
  meetingsToday: number
}

export type Account = {
  id: string
  company: string
  contact: string
  owner: string
  stage: Stage
  value: number
  nextStep: string
  lastTouch: string
  renewal: string
  activeProjects: number
  health: 'Stable' | 'Watch' | 'Urgent'
}

export type Meeting = {
  id: string
  calendar: CalendarId
  title: string
  account: string
  owner: string
  // The appointed host of the event (organizer). When present, this is the
  // authoritative host to display; `owner` is kept for back-compat.
  host?: string
  organizerEmail?: string
  date: string
  // Inclusive last day (YYYY-MM-DD) of a multi-day ALL-DAY span (time_off /
  // holiday / company-event). Absent or equal to `date` for single-day or timed
  // events. Used by the calendar to render the banner on every day in range.
  endDate?: string
  time: string
  duration: string
  type: string
  location: string
  parsedAddress?: string | null
  matchedClientId?: string | null
  storedClientId?: string | null
  clientInvited?: boolean
  clientEmail?: string | null
  clientName?: string | null
  attendeeEmails?: string[]
  dealId?: number | null
  description?: string | null
  // NULL = normal appointment; 'time_off' = approved time off; 'holiday' = company holiday banner.
  category?: 'time_off' | 'holiday' | null
  // Set when this is a company-holiday occurrence that belongs to a recurring series.
  // parentHolidayId = the company_holidays.id of the is_recurrence_parent=1 rule row.
  // holidayId = the company_holidays.id of this specific occurrence/child row.
  parentHolidayId?: number | null
  holidayId?: number | null
}

export type Project = {
  id: string
  name: string
  account: string
  owner: string
  phase: 'Planning' | 'Execution' | 'Launch' | 'Support'
  progress: number
  health: ProjectHealth
  dueDate: string
  spend: string
  nextMilestone: string
}

export type ProjectTask = {
  id: string
  title: string
  project: string
  assignee: string
  lane: ProjectLane
  dueDate: string
}

export const calendarSources: CalendarSource[] = [
  { id: 'estimates', name: 'Scheduled Estimates', color: '#ed6c02' },
  { id: 'projects', name: 'Project Calendar', color: '#2e7d32' },
  { id: 'internal', name: 'Internal Meetings', color: '#1976d2' },
  // Subcontractor PROJECT START DATES (from SUB_DB portal_projects.start_date).
  // Synthetic, admin/super_admin-only layer merged server-side into the calendar
  // feed. Each event carries an explicit source tag ('sub_project_starts') so the
  // frontend convert preserves this calendar rather than re-classifying by title.
  { id: 'sub_project_starts', name: 'Subcontractor Project Starts', color: '#9c27b0' },
]

export const employeeColors: Record<string, string> = {
  // Real BotPros employees — every person gets a DISTINCT hue. Keyed by
  // full name; App.tsx derives a first-name map from these automatically.
  'Maxwell Ritchie': '#1976d2',
  // Mock employees (fallback)
  'Avery Sloan': '#1976d2',
  'Jordan Lee': '#7b1fa2',
  'Mina Ortiz': '#c2185b',
  'Theo Barnes': '#00796b',
}

export const teamMembers: TeamMember[] = [
  { name: 'Avery Sloan', role: 'Estimator', accounts: 8, meetingsToday: 3 },
  { name: 'Jordan Lee', role: 'Project manager', accounts: 5, meetingsToday: 2 },
  { name: 'Mina Ortiz', role: 'Estimator', accounts: 4, meetingsToday: 2 },
  { name: 'Theo Barnes', role: 'Field supervisor', accounts: 6, meetingsToday: 1 },
]

export const accounts: Account[] = [
  {
    id: 'AC-104',
    company: 'Marrow & Finch',
    contact: 'Naomi Patel',
    owner: 'Avery Sloan',
    stage: 'Negotiation',
    value: 78000,
    nextStep: 'Finalize onboarding scope and sign annual services agreement',
    lastTouch: '2026-03-07',
    renewal: '2026-09-30',
    activeProjects: 2,
    health: 'Watch',
  },
  {
    id: 'AC-118',
    company: 'Cinder Logistics',
    contact: 'Eli Grant',
    owner: 'Jordan Lee',
    stage: 'Proposal',
    value: 46500,
    nextStep: 'Deliver staffing model and implementation estimate',
    lastTouch: '2026-03-08',
    renewal: '2026-07-14',
    activeProjects: 1,
    health: 'Stable',
  },
  {
    id: 'AC-123',
    company: 'Bayside Surgical',
    contact: 'Dr. Lila Chen',
    owner: 'Theo Barnes',
    stage: 'Active',
    value: 112000,
    nextStep: 'Approve Q2 training schedule and billing migration plan',
    lastTouch: '2026-03-09',
    renewal: '2026-12-12',
    activeProjects: 3,
    health: 'Urgent',
  },
  {
    id: 'AC-137',
    company: 'Northward Energy',
    contact: 'Samir Ochoa',
    owner: 'Mina Ortiz',
    stage: 'Qualified',
    value: 59000,
    nextStep: 'Run process mapping workshop with finance and operations',
    lastTouch: '2026-03-04',
    renewal: '2026-11-03',
    activeProjects: 1,
    health: 'Stable',
  },
  {
    id: 'AC-141',
    company: 'Harbor Assembly',
    contact: 'Mara Ellis',
    owner: 'Avery Sloan',
    stage: 'Lead',
    value: 22000,
    nextStep: 'Confirm discovery attendees and current CRM pain points',
    lastTouch: '2026-03-03',
    renewal: '2026-10-06',
    activeProjects: 0,
    health: 'Stable',
  },
  {
    id: 'AC-152',
    company: 'Oakwell Housing',
    contact: 'James Rowan',
    owner: 'Jordan Lee',
    stage: 'Active',
    value: 93500,
    nextStep: 'Close punch list before tenant portal launch',
    lastTouch: '2026-03-06',
    renewal: '2026-08-18',
    activeProjects: 2,
    health: 'Watch',
  },
]

export const meetings: Meeting[] = [
  // ═══════════════════════════════════════
  // SCHEDULED ESTIMATES — estimator visits
  // ═══════════════════════════════════════
  {
    id: 'SE-01',
    calendar: 'estimates',
    title: 'Interior estimate — Henderson residence',
    account: 'Henderson Residence',
    owner: 'Avery Sloan',
    date: '2026-03-09',
    time: '09:00',
    duration: '90m',
    type: 'Interior',
    location: '1420 Oak Dr',
  },
  {
    id: 'SE-02',
    calendar: 'estimates',
    title: 'Exterior estimate — Bayside Medical',
    account: 'Bayside Medical Center',
    owner: 'Jordan Lee',
    date: '2026-03-10',
    time: '10:00',
    duration: '120m',
    type: 'Exterior',
    location: '850 Harbor Blvd',
  },
  {
    id: 'SE-03',
    calendar: 'estimates',
    title: 'Commercial walkthrough — Harbor Assembly',
    account: 'Harbor Assembly',
    owner: 'Theo Barnes',
    date: '2026-03-11',
    time: '14:00',
    duration: '90m',
    type: 'Commercial',
    location: 'Harbor Industrial Park',
  },
  {
    id: 'SE-04',
    calendar: 'estimates',
    title: 'Interior estimate — Northward Energy HQ',
    account: 'Northward Energy',
    owner: 'Mina Ortiz',
    date: '2026-03-12',
    time: '09:30',
    duration: '60m',
    type: 'Interior',
    location: '220 Commerce St',
  },
  {
    id: 'SE-05',
    calendar: 'estimates',
    title: 'Exterior estimate — Cinder warehouse',
    account: 'Cinder Logistics',
    owner: 'Avery Sloan',
    date: '2026-03-13',
    time: '11:00',
    duration: '120m',
    type: 'Exterior',
    location: '1890 Freight Way',
  },
  {
    id: 'SE-06',
    calendar: 'estimates',
    title: 'Re-estimate — Oakwell Block C',
    account: 'Oakwell Housing',
    owner: 'Jordan Lee',
    date: '2026-03-16',
    time: '08:30',
    duration: '90m',
    type: 'Re-estimate',
    location: '45 Elm Street',
  },
  {
    id: 'SE-07',
    calendar: 'estimates',
    title: 'Color consultation — Marrow & Finch',
    account: 'Marrow & Finch',
    owner: 'Mina Ortiz',
    date: '2026-03-17',
    time: '13:00',
    duration: '60m',
    type: 'Consultation',
    location: 'Client office',
  },
  {
    id: 'SE-08',
    calendar: 'estimates',
    title: 'Interior estimate — Davis Group',
    account: 'Davis Group',
    owner: 'Avery Sloan',
    date: '2026-03-19',
    time: '10:00',
    duration: '90m',
    type: 'Interior',
    location: '700 Pine Ave',
  },
  {
    id: 'SE-09',
    calendar: 'estimates',
    title: 'Exterior estimate — Summit Real Estate',
    account: 'Summit Real Estate',
    owner: 'Theo Barnes',
    date: '2026-03-20',
    time: '14:00',
    duration: '120m',
    type: 'Exterior',
    location: '1200 Summit Dr',
  },
  {
    id: 'SE-10',
    calendar: 'estimates',
    title: 'Commercial estimate — City Center Mall',
    account: 'City Center Mall',
    owner: 'Jordan Lee',
    date: '2026-03-23',
    time: '09:00',
    duration: '120m',
    type: 'Commercial',
    location: '100 Main St',
  },

  // ═══════════════════════════════════════
  // PROJECT CALENDAR — crew starts, inspections, milestones
  // ═══════════════════════════════════════
  {
    id: 'PE-01',
    calendar: 'projects',
    title: 'Crew start — Bayside exterior',
    account: 'Bayside Surgical',
    owner: 'Theo Barnes',
    date: '2026-03-09',
    time: '07:00',
    duration: '8h',
    type: 'Crew start',
    location: '850 Harbor Blvd',
  },
  {
    id: 'PE-02',
    calendar: 'projects',
    title: 'QC inspection — Henderson interior',
    account: 'Henderson Residence',
    owner: 'Jordan Lee',
    date: '2026-03-10',
    time: '15:00',
    duration: '60m',
    type: 'QC inspection',
    location: '1420 Oak Dr',
  },
  {
    id: 'PE-03',
    calendar: 'projects',
    title: 'Material delivery — Oakwell Block A',
    account: 'Oakwell Housing',
    owner: 'Mina Ortiz',
    date: '2026-03-11',
    time: '08:00',
    duration: '2h',
    type: 'Delivery',
    location: '45 Elm Street',
  },
  {
    id: 'PE-04',
    calendar: 'projects',
    title: 'Final walkthrough — Northward lobby',
    account: 'Northward Energy',
    owner: 'Theo Barnes',
    date: '2026-03-12',
    time: '16:00',
    duration: '90m',
    type: 'Walkthrough',
    location: '220 Commerce St',
  },
  {
    id: 'PE-05',
    calendar: 'projects',
    title: 'Punch list review — Cinder warehouse',
    account: 'Cinder Logistics',
    owner: 'Avery Sloan',
    date: '2026-03-14',
    time: '10:00',
    duration: '60m',
    type: 'Punch list',
    location: '1890 Freight Way',
  },
  {
    id: 'PE-06',
    calendar: 'projects',
    title: 'Crew start — Marrow & Finch offices',
    account: 'Marrow & Finch',
    owner: 'Jordan Lee',
    date: '2026-03-17',
    time: '07:00',
    duration: '8h',
    type: 'Crew start',
    location: '200 Legal Dr',
  },
  {
    id: 'PE-07',
    calendar: 'projects',
    title: 'Mid-project inspection — Bayside',
    account: 'Bayside Surgical',
    owner: 'Theo Barnes',
    date: '2026-03-19',
    time: '14:00',
    duration: '90m',
    type: 'Inspection',
    location: '850 Harbor Blvd',
  },
  {
    id: 'PE-08',
    calendar: 'projects',
    title: 'Project closeout — Henderson',
    account: 'Henderson Residence',
    owner: 'Avery Sloan',
    date: '2026-03-24',
    time: '09:00',
    duration: '2h',
    type: 'Closeout',
    location: '1420 Oak Dr',
  },
  {
    id: 'PE-09',
    calendar: 'projects',
    title: 'Client sign-off — Oakwell Block A',
    account: 'Oakwell Housing',
    owner: 'Mina Ortiz',
    date: '2026-03-26',
    time: '11:00',
    duration: '60m',
    type: 'Sign-off',
    location: '45 Elm Street',
  },

  // ═══════════════════════════════════════
  // INTERNAL MEETINGS — calls, reviews, workshops
  // ═══════════════════════════════════════
  {
    id: 'ME-201',
    calendar: 'internal',
    title: 'Executive handoff review',
    account: 'Bayside Surgical',
    owner: 'Theo Barnes',
    date: '2026-03-09',
    time: '08:30',
    duration: '45m',
    type: 'Review',
    location: 'Conference room A',
  },
  {
    id: 'ME-202',
    calendar: 'internal',
    title: 'Onboarding scope call',
    account: 'Marrow & Finch',
    owner: 'Avery Sloan',
    date: '2026-03-09',
    time: '10:00',
    duration: '60m',
    type: 'Call',
    location: 'Google Meet',
  },
  {
    id: 'ME-203',
    calendar: 'internal',
    title: 'Billing migration workshop',
    account: 'Bayside Surgical',
    owner: 'Mina Ortiz',
    date: '2026-03-09',
    time: '13:30',
    duration: '90m',
    type: 'Workshop',
    location: 'Operations room',
  },
  {
    id: 'ME-204',
    calendar: 'internal',
    title: 'Proposal walkthrough',
    account: 'Cinder Logistics',
    owner: 'Jordan Lee',
    date: '2026-03-10',
    time: '09:00',
    duration: '30m',
    type: 'Call',
    location: 'Zoom',
  },
  {
    id: 'ME-205',
    calendar: 'internal',
    title: 'Process mapping session',
    account: 'Northward Energy',
    owner: 'Mina Ortiz',
    date: '2026-03-11',
    time: '11:00',
    duration: '60m',
    type: 'Workshop',
    location: 'Google Meet',
  },
  {
    id: 'ME-206',
    calendar: 'internal',
    title: 'Tenant portal launch review',
    account: 'Oakwell Housing',
    owner: 'Jordan Lee',
    date: '2026-03-12',
    time: '14:00',
    duration: '45m',
    type: 'Review',
    location: 'Client HQ',
  },
  {
    id: 'ME-207',
    calendar: 'internal',
    title: 'Discovery prep',
    account: 'Harbor Assembly',
    owner: 'Avery Sloan',
    date: '2026-03-16',
    time: '09:30',
    duration: '30m',
    type: 'Call',
    location: 'Phone',
  },
  {
    id: 'ME-208',
    calendar: 'internal',
    title: 'Operations floor visit',
    account: 'Northward Energy',
    owner: 'Theo Barnes',
    date: '2026-03-18',
    time: '15:00',
    duration: '120m',
    type: 'On-site',
    location: 'Northward plant',
  },
  {
    id: 'ME-209',
    calendar: 'internal',
    title: 'Quarterly business review',
    account: 'Oakwell Housing',
    owner: 'Theo Barnes',
    date: '2026-03-23',
    time: '10:30',
    duration: '60m',
    type: 'Review',
    location: 'Zoom',
  },
  {
    id: 'ME-210',
    calendar: 'internal',
    title: 'Contract close session',
    account: 'Marrow & Finch',
    owner: 'Avery Sloan',
    date: '2026-03-26',
    time: '16:00',
    duration: '45m',
    type: 'Call',
    location: 'Google Meet',
  },
  {
    id: 'ME-211',
    calendar: 'internal',
    title: 'Renewal strategy call',
    account: 'Bayside Surgical',
    owner: 'Avery Sloan',
    date: '2026-03-09',
    time: '09:15',
    duration: '30m',
    type: 'Call',
    location: 'Zoom',
  },
  {
    id: 'ME-212',
    calendar: 'internal',
    title: 'Compliance audit follow-up',
    account: 'Northward Energy',
    owner: 'Jordan Lee',
    date: '2026-03-09',
    time: '11:00',
    duration: '45m',
    type: 'Review',
    location: 'Conference room B',
  },
  {
    id: 'ME-213',
    calendar: 'internal',
    title: 'Facilities walkthrough',
    account: 'Harbor Assembly',
    owner: 'Theo Barnes',
    date: '2026-03-09',
    time: '14:30',
    duration: '60m',
    type: 'On-site',
    location: 'Harbor plant',
  },
  {
    id: 'ME-214',
    calendar: 'internal',
    title: 'Tenant onboarding review',
    account: 'Oakwell Housing',
    owner: 'Mina Ortiz',
    date: '2026-03-09',
    time: '15:45',
    duration: '30m',
    type: 'Review',
    location: 'Google Meet',
  },
  {
    id: 'ME-215',
    calendar: 'internal',
    title: 'Budget alignment workshop',
    account: 'Cinder Logistics',
    owner: 'Avery Sloan',
    date: '2026-03-10',
    time: '10:30',
    duration: '60m',
    type: 'Workshop',
    location: 'Operations room',
  },
  {
    id: 'ME-216',
    calendar: 'internal',
    title: 'Staffing model review',
    account: 'Cinder Logistics',
    owner: 'Jordan Lee',
    date: '2026-03-10',
    time: '13:00',
    duration: '45m',
    type: 'Review',
    location: 'Conference room A',
  },
  {
    id: 'ME-217',
    calendar: 'internal',
    title: 'Fleet tracking check-in',
    account: 'Northward Energy',
    owner: 'Theo Barnes',
    date: '2026-03-10',
    time: '15:30',
    duration: '30m',
    type: 'Call',
    location: 'Phone',
  },
  {
    id: 'ME-218',
    calendar: 'internal',
    title: 'Safety compliance walkthrough',
    account: 'Harbor Assembly',
    owner: 'Mina Ortiz',
    date: '2026-03-11',
    time: '09:00',
    duration: '90m',
    type: 'On-site',
    location: 'Harbor plant',
  },
  {
    id: 'ME-219',
    calendar: 'internal',
    title: 'Quarterly forecast call',
    account: 'Marrow & Finch',
    owner: 'Avery Sloan',
    date: '2026-03-11',
    time: '14:00',
    duration: '30m',
    type: 'Call',
    location: 'Zoom',
  },
  {
    id: 'ME-220',
    calendar: 'internal',
    title: 'Vendor coordination workshop',
    account: 'Oakwell Housing',
    owner: 'Jordan Lee',
    date: '2026-03-12',
    time: '10:00',
    duration: '60m',
    type: 'Workshop',
    location: 'Operations room',
  },
  {
    id: 'ME-221',
    calendar: 'internal',
    title: 'Insurance documentation review',
    account: 'Bayside Surgical',
    owner: 'Theo Barnes',
    date: '2026-03-12',
    time: '16:00',
    duration: '30m',
    type: 'Review',
    location: 'Google Meet',
  },
  {
    id: 'ME-222',
    calendar: 'internal',
    title: 'Permit status check-in',
    account: 'Harbor Assembly',
    owner: 'Avery Sloan',
    date: '2026-03-13',
    time: '08:30',
    duration: '30m',
    type: 'Call',
    location: 'Phone',
  },
  {
    id: 'ME-223',
    calendar: 'internal',
    title: 'Data migration workshop',
    account: 'Cinder Logistics',
    owner: 'Mina Ortiz',
    date: '2026-03-13',
    time: '11:00',
    duration: '90m',
    type: 'Workshop',
    location: 'Client HQ',
  },
  {
    id: 'ME-224',
    calendar: 'internal',
    title: 'Executive sponsor briefing',
    account: 'Northward Energy',
    owner: 'Jordan Lee',
    date: '2026-03-13',
    time: '14:00',
    duration: '45m',
    type: 'Call',
    location: 'Zoom',
  },
  {
    id: 'ME-225',
    calendar: 'internal',
    title: 'Equipment inspection visit',
    account: 'Oakwell Housing',
    owner: 'Theo Barnes',
    date: '2026-03-14',
    time: '09:00',
    duration: '120m',
    type: 'On-site',
    location: 'Oakwell campus',
  },
  {
    id: 'ME-226',
    calendar: 'internal',
    title: 'Change order review',
    account: 'Marrow & Finch',
    owner: 'Mina Ortiz',
    date: '2026-03-14',
    time: '13:30',
    duration: '45m',
    type: 'Review',
    location: 'Conference room A',
  },
  {
    id: 'ME-227',
    calendar: 'internal',
    title: 'Subcontractor alignment call',
    account: 'Bayside Surgical',
    owner: 'Avery Sloan',
    date: '2026-03-14',
    time: '16:00',
    duration: '30m',
    type: 'Call',
    location: 'Google Meet',
  },
  {
    id: 'ME-228',
    calendar: 'internal',
    title: 'Project kickoff workshop',
    account: 'Harbor Assembly',
    owner: 'Jordan Lee',
    date: '2026-03-17',
    time: '10:00',
    duration: '90m',
    type: 'Workshop',
    location: 'Operations room',
  },
  {
    id: 'ME-229',
    calendar: 'internal',
    title: 'Site readiness inspection',
    account: 'Cinder Logistics',
    owner: 'Theo Barnes',
    date: '2026-03-17',
    time: '14:00',
    duration: '60m',
    type: 'On-site',
    location: 'Cinder warehouse',
  },
  {
    id: 'ME-230',
    calendar: 'internal',
    title: 'Risk register review',
    account: 'Northward Energy',
    owner: 'Mina Ortiz',
    date: '2026-03-19',
    time: '09:30',
    duration: '45m',
    type: 'Review',
    location: 'Zoom',
  },
  {
    id: 'ME-231',
    calendar: 'internal',
    title: 'Client success check-in',
    account: 'Marrow & Finch',
    owner: 'Theo Barnes',
    date: '2026-03-20',
    time: '11:00',
    duration: '30m',
    type: 'Call',
    location: 'Phone',
  },
  {
    id: 'ME-232',
    calendar: 'internal',
    title: 'Go-live readiness workshop',
    account: 'Oakwell Housing',
    owner: 'Jordan Lee',
    date: '2026-03-20',
    time: '14:00',
    duration: '60m',
    type: 'Workshop',
    location: 'Operations room',
  },
]

export const projects: Project[] = [
  {
    id: 'PR-301',
    name: 'Client portal rollout',
    account: 'Marrow & Finch',
    owner: 'Jordan Lee',
    phase: 'Execution',
    progress: 74,
    health: 'At risk',
    dueDate: '2026-03-28',
    spend: '$42k of $55k',
    nextMilestone: 'UAT sign-off',
  },
  {
    id: 'PR-302',
    name: 'Billing migration',
    account: 'Bayside Surgical',
    owner: 'Mina Ortiz',
    phase: 'Execution',
    progress: 61,
    health: 'Needs review',
    dueDate: '2026-03-21',
    spend: '$68k of $80k',
    nextMilestone: 'Finance approval',
  },
  {
    id: 'PR-303',
    name: 'Success playbook refresh',
    account: 'Oakwell Housing',
    owner: 'Theo Barnes',
    phase: 'Planning',
    progress: 32,
    health: 'On track',
    dueDate: '2026-04-04',
    spend: '$14k of $24k',
    nextMilestone: 'Content review',
  },
  {
    id: 'PR-304',
    name: 'Operational audit',
    account: 'Northward Energy',
    owner: 'Avery Sloan',
    phase: 'Launch',
    progress: 86,
    health: 'On track',
    dueDate: '2026-03-19',
    spend: '$33k of $34k',
    nextMilestone: 'Field handoff',
  },
  {
    id: 'PR-305',
    name: 'Renewal readiness',
    account: 'Bayside Surgical',
    owner: 'Theo Barnes',
    phase: 'Support',
    progress: 54,
    health: 'Needs review',
    dueDate: '2026-03-24',
    spend: '$18k of $28k',
    nextMilestone: 'Reference calls',
  },
  {
    id: 'PR-306',
    name: 'Discovery sprint',
    account: 'Harbor Assembly',
    owner: 'Avery Sloan',
    phase: 'Planning',
    progress: 18,
    health: 'On track',
    dueDate: '2026-03-30',
    spend: '$5k of $16k',
    nextMilestone: 'Workshop agenda',
  },
]

export const projectTasks: ProjectTask[] = [
  {
    id: 'TK-401',
    title: 'Map invoice fields for import',
    project: 'Billing migration',
    assignee: 'Mina Ortiz',
    lane: 'In progress',
    dueDate: '2026-03-11',
  },
  {
    id: 'TK-402',
    title: 'Confirm sandbox credentials with client IT',
    project: 'Client portal rollout',
    assignee: 'Jordan Lee',
    lane: 'Review',
    dueDate: '2026-03-10',
  },
  {
    id: 'TK-403',
    title: 'Draft workshop agenda',
    project: 'Discovery sprint',
    assignee: 'Avery Sloan',
    lane: 'Backlog',
    dueDate: '2026-03-15',
  },
  {
    id: 'TK-404',
    title: 'Collect training sign-off notes',
    project: 'Renewal readiness',
    assignee: 'Theo Barnes',
    lane: 'In progress',
    dueDate: '2026-03-13',
  },
  {
    id: 'TK-405',
    title: 'Reconcile launch checklist',
    project: 'Operational audit',
    assignee: 'Jordan Lee',
    lane: 'Done',
    dueDate: '2026-03-08',
  },
  {
    id: 'TK-406',
    title: 'Prepare executive status summary',
    project: 'Client portal rollout',
    assignee: 'Theo Barnes',
    lane: 'Backlog',
    dueDate: '2026-03-12',
  },
  {
    id: 'TK-407',
    title: 'QA the tenant portal permissions',
    project: 'Success playbook refresh',
    assignee: 'Mina Ortiz',
    lane: 'Review',
    dueDate: '2026-03-18',
  },
  {
    id: 'TK-408',
    title: 'Approve finance rollback plan',
    project: 'Billing migration',
    assignee: 'Avery Sloan',
    lane: 'Backlog',
    dueDate: '2026-03-14',
  },
]
